import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';
import {Bar, BarChart, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

const STAGE_COLORS_MAP = {applied: '#9ca3af', screening: '#f59e0b', interview: '#3b82f6', offer: '#8b5cf6', hired: '#22c55e', rejected: '#ef4444'};

export default function Dashboard() {
  const [reports, setReports] = useState(null);
  const [applicants, setApplicants] = useState([]);

  useEffect(() => {
    api('/ats/reports').then(r => r.ok ? r.json() : null).then(setReports).catch(() => {});
    api('/ats/applicants').then(r => r.ok ? r.json() : []).then(setApplicants).catch(() => {});
  }, []);

  const stageData = reports?.byStage ? Object.entries(reports.byStage).map(([name, count]) => ({name, count})) : [];
  const sourceData = reports?.bySource ? Object.entries(reports.bySource).map(([name, count]) => ({name, count})).sort((a, b) => b.count - a.count) : [];
  const trendData = reports?.applicationTrend?.map(p => ({date: p.date.slice(5), count: p.count})) || [];

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          {label: 'Open Positions', value: reports?.openJobs ?? 0, color: 'text-teal-600'},
          {label: 'Total Applicants', value: reports?.totalApplicants ?? 0, color: 'text-blue-600'},
          {label: 'Avg Time to Hire', value: reports?.avgTimeToHireDays != null ? `${reports.avgTimeToHireDays}d` : '—', color: 'text-amber-600'},
          {label: 'Hired', value: reports?.byStage?.hired ?? 0, color: 'text-green-600'},
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">{stat.label}</p>
            <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Stage Funnel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Funnel</h3>
          {stageData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stageData} layout="vertical" margin={{left: 60}}>
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" tick={{fontSize: 12}} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stageData.map((entry, i) => (
                    <Cell key={i} fill={STAGE_COLORS_MAP[entry.name] || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet.</p>}
        </div>

        {/* Source Effectiveness */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Source</h3>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={sourceData} layout="vertical" margin={{left: 80}}>
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" tick={{fontSize: 12}} />
                <Tooltip />
                <Bar dataKey="count" fill="#14b8a6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet.</p>}
        </div>
      </div>

      {/* Application Trend */}
      {trendData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Applications (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <XAxis dataKey="date" tick={{fontSize: 11}} interval={4} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Applicants */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Applicants</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applicants.slice(0, 10).map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4"><Link to={`/ats/applicants/${a.id}`} className="text-teal-600 hover:text-teal-800 font-medium">{a.firstName} {a.lastName}</Link></td>
                  <td className="px-6 py-4 text-gray-600">{a.jobTitle || '—'}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                      a.stage === 'hired' ? 'bg-green-50 text-green-700' :
                      a.stage === 'interview' ? 'bg-blue-50 text-blue-700' :
                      a.stage === 'offer' ? 'bg-purple-50 text-purple-700' :
                      a.stage === 'rejected' ? 'bg-red-50 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{a.stage}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {applicants.length === 0 && <div className="text-center py-12 text-gray-500">No applicants yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
